"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import { createPortal } from "react-dom";
import { Expand } from "lucide-react";

import { useTransientScrollState } from "@/components/site/layout/use-transient-scroll-state";
import { OverviewRoadmapBoard } from "@/components/site/sections/overview-board";
import { cn } from "@/lib/utils";

const OverviewRoadmapModal = dynamic(
  () =>
    import("@/components/site/sections/overview-roadmap/modal").then(
      (module) => module.OverviewRoadmapModal,
    ),
  {
    ssr: false,
  },
);

/** The Overview roadmap on its own: the same board, data and step window as the Overview preview. */
export function OverviewRoadmapWidget() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const { isScrolling, handleScroll } = useTransientScrollState();

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <>
      <div className="group/roadmap relative mt-6 overflow-hidden rounded-[1.55rem] border border-border/60 bg-muted/18 shadow-[0_14px_40px_-34px_rgba(15,23,42,0.16)] transition-[border-color,box-shadow] duration-300 hover:border-border/80 hover:shadow-[0_22px_54px_-34px_rgba(15,23,42,0.2)] md:mt-7">
        <div
          data-scroll-active={isScrolling ? "true" : "false"}
          onScroll={handleScroll}
          className="site-scrollbar-auto max-h-[calc(24rem-55px)] overflow-auto overscroll-contain px-4 pt-4 pb-16 [scrollbar-gutter:stable] sm:px-5 sm:pt-5 md:max-h-[calc(25rem-55px)]"
        >
          <OverviewRoadmapBoard
            headingLevel={4}
            lanesBleedClassName="-mx-4 px-4 scroll-px-4 sm:-mx-5 sm:px-5 sm:scroll-px-5"
          />
        </div>

        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-14 rounded-b-[1.55rem] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(250,250,250,0.96)_100%)]"
        />
        <button
          type="button"
          aria-label="Open roadmap in full screen"
          onClick={() => setIsOpen(true)}
          className={cn(
            "site-pressable absolute right-4 bottom-4 z-[50] inline-flex size-11 items-center justify-center rounded-full border border-border/75 bg-background/98 text-foreground backdrop-blur-sm transition-transform duration-300 group-hover/roadmap:-translate-y-0.5 animate-[roadmap-fullscreen-pulse_1.55s_ease-in-out_infinite]",
            "[box-shadow:0_14px_30px_-18px_rgba(15,23,42,0.24),0_0_0_7px_rgba(255,255,255,0.92)]",
          )}
        >
          <Expand className="size-4" />
        </button>
      </div>

      {mounted && isOpen
        ? createPortal(
            <OverviewRoadmapModal onClose={() => setIsOpen(false)} />,
            document.body,
          )
        : null}
    </>
  );
}
